import { useMemo } from 'react';

import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { locationsApi } from '../axios/locations';
import { Location } from '../types';

export function useLocationsInfiniteQuery(searchQuery: string) {
  return useInfiniteQuery({
    queryKey: ['locations', searchQuery],
    queryFn: ({ pageParam = 1 }) => locationsApi.getLocationsWithSearch(pageParam, searchQuery),
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.pages) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    getPreviousPageParam: (firstPage) => {
      if (firstPage.page > 1) {
        return firstPage.page - 1;
      }
      return undefined;
    },
    initialPageParam: 1,
  });
}

export function useLocationQuery(locationId: Location['id']) {
  return useQuery({
    queryKey: ['location', locationId],
    queryFn: () => locationsApi.getLocation(locationId),
    enabled: !!locationId,
  });
}

// The IDs travel in the URL path (`/react_get_locations_by_ids/:ids`), so resolving a
// large set at once (e.g. every location across a big findings list) can blow past
// server URI limits and get a 414. Split into bounded chunks and merge, deduping
// internally so callers don't have to.
const LOCATIONS_BY_IDS_CHUNK_SIZE = 100;

interface LocationsByIdsQueryOptions {
  // Opt-in only: the shared hook has 7 pre-existing callers, and both policies change
  // behavior for them. `keepPreviousData` makes a disabled query keep serving stale
  // data; `retry` (> 0) enables a single per-chunk retry. Off by default so callers
  // that didn't ask keep the app-wide `retry: 0` and no placeholder.
  keepPreviousData?: boolean;
  retry?: number;
}

function isClientError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  return status !== undefined && status >= 400 && status < 500;
}

export function useLocationsByIdsQuery(
  locationIds: number[],
  options?: LocationsByIdsQueryOptions,
) {
  const sortedIds = useMemo(
    () =>
      locationIds && locationIds.length > 0
        ? Array.from(new Set(locationIds)).sort((a, b) => a - b)
        : [],
    [locationIds],
  );
  const retryChunks = (options?.retry ?? 0) > 0;

  return useQuery({
    queryKey: ['locationsByIds', sortedIds],
    queryFn: async () => {
      const chunks: number[][] = [];
      for (let index = 0; index < sortedIds.length; index += LOCATIONS_BY_IDS_CHUNK_SIZE) {
        chunks.push(sortedIds.slice(index, index + LOCATIONS_BY_IDS_CHUNK_SIZE));
      }
      // When opted in, retry each chunk once on its own so a single flaky request
      // doesn't drop ~100 names; then allSettled keeps whatever resolved. A 4xx (incl.
      // the 414 the chunking exists to avoid) can't succeed on a second try, so it's
      // not retried. This is the only retry layer -- the query-level retry stays 0.
      const fetchChunk = (idsCsv: string) => {
        const request = locationsApi.getLocationsByIds(idsCsv);
        if (!retryChunks) return request;
        return request.catch((error) => {
          if (isClientError(error)) throw error;
          return locationsApi.getLocationsByIds(idsCsv);
        });
      };
      const results = await Promise.allSettled(chunks.map((chunk) => fetchChunk(chunk.join(','))));
      const fulfilled = results
        .filter((result): result is PromiseFulfilledResult<Location[]> => result.status === 'fulfilled')
        .map((result) => result.value);
      if (fulfilled.length === 0 && chunks.length > 0) {
        throw (results[0] as PromiseRejectedResult).reason;
      }
      return fulfilled.flat();
    },
    enabled: sortedIds.length > 0,
    // The per-chunk retry above already covers a full outage, so we don't stack a
    // query-level retry on top; the app-wide `retry: 0` (App.tsx) applies.
    retry: 0,
    // Guard on a non-empty set: a disabled (empty-set) query stays `pending` forever,
    // and keepPreviousData would then serve the last result permanently.
    placeholderData:
      options?.keepPreviousData && sortedIds.length > 0 ? keepPreviousData : undefined,
  });
}
